import { useCallback, useEffect, useMemo, useRef } from "react";
import EmailValidator from "email-validator";
import { useQuery } from "@apollo/client";
import { isArray, isEmpty } from "lodash";

import useForm, { FieldList } from "core/hooks/useForm";
import { isValidPhoneNumber } from "core/includes/phoneNumber";
import { countryToPhoneCode, getCountryFromCode } from "theme/utils";
import { getEventRegistration } from "core/includes/queries";
import usePhoneNumberInput from "pages/Components/CartAndCheckout/hooks/usePhoneNumberInput";
import useCart from "pages/Components/CartPage/hooks/useCart";
import { useAppState } from "core/contexts/AppContext";

type Props = {
    validateBillingFields?: boolean,
}

export const ADDITIONAL_FIELD: FieldList = {
    firstRow: {
        fields: {
            firstName: {
                label: 'First name',
                isRequired: true,
            },
            lastName: {
                label: 'Last name',
                isRequired: true,
            },
            firstNameBilling: {
                label: 'First name',
                isRequired: true,
                isBilling: true,
            },
            lastNameBilling: {
                label: 'Last name',
                isRequired: true,
                isBilling: true,
            },
        },
        isRow: true,
    },
    secondRow: {
        fields: {
            email: {
                label: 'Email',
                isRequired: true,
            },
            phone: {
                label: 'Mobile number',
                isRequired: true,
            },
        },
        isRow: true,
    },
    company: {
        label: 'Company'
    },
    addressSearch: {
        label: 'Find address',
        isRequired: false,
    },
    addressSearchBilling: {
        label: 'Find address',
        isBilling: true,
    },
    addressOne: {
        label: 'Address line 1',
        isRequired: true,
    },
    addressOneBilling: {
        label: 'Address line 1',
        isRequired: true,
        isBilling: true,
    },
    addressTwo: {
        label: 'Address line 2'
    },
    addressTwoBilling: {
        label: 'Address line 2',
        isBilling: true,
    },
    thirdRow: {
        fields: {
            city: {
                label: 'City',
                isRequired: true,
            },
            state: {
                label: 'State/Region',
            },
            cityBilling: {
                label: 'City',
                isRequired: true,
                isBilling: true,
            },
            stateBilling: {
                label: 'State/Region',
                isBilling: true,
            },
        },
        isRow: true,
    },
    fourthRow: {
        fields: {
            country: {
                label: 'Country',
                isRequired: true,
            },
            postCode: {
                label: 'Post code',
                isRequired: true,
            },
            countryBilling: {
                label: 'Country',
                isRequired: true,
                isBilling: true,
            },
            postCodeBilling: {
                label: 'Post code',
                isRequired: true,
                isBilling: true,
            },
        },
        isRow: true,
    }
};

export const BILLING_FIELDS_TO_USE = [
    'firstName',
    'lastName',
    'city',
    'country',
    'postCode',
    'addressSearch',
    'addressOne',
    'addressTwo',
    'state',
];

export const MANUAL_ADDRESS_FIELDS_FOR_SHIPPING = [
    'city',
    'country',
    'postCode',
    'addressSearch',
    'addressOne',
    'addressTwo',
    'state',
];

const useDetailsForm = ({ validateBillingFields = false }: Props) => {
    const prevEventData = useRef(null);
    const { getCountry } = useCart();
    const countryCode = getCountry();
    const [appState] = useAppState();

    const validator = useCallback((fields: FieldList, isSubmit?: boolean) => {
        const errors: any = {};

        if (!fields.email) {
            errors.email = "Please provide an email.";
        } else if (!EmailValidator.validate(fields.email) && isSubmit) {
            errors.email = "Please provide a valid email.";
        }

        if (!fields.firstName) {
            errors.firstName = "Please provide a first name.";
        }

        if (!fields.lastName) {
            errors.lastName = "Please provide a last name.";
        }

        if (!fields.phone && !fields.phoneCode) {
            errors.phone = "Please provide a mobile number.";
        } else if (fields.phoneCode && !isValidPhoneNumber(countryToPhoneCode(fields.phoneCode) + fields.phone) && isSubmit) {
            errors.phone = "Please provide a valid phone number.";
        }

        if (!fields.phoneCode && isSubmit) {
            errors.phone = "Please provide a phone code.";
        }

        if (!fields.city) {
            errors.city = "Please provide a city.";
        }

        if (!fields.country) {
            errors.country = "Please provide a country.";
        }

        if (!fields.postCode) {
            errors.postCode = "Please provide a post code.";
        }

        if (!fields.addressOne && !fields.addressTwo) {
            errors.addressOne = "Please provide an address.";
        }

        if (isSubmit && !fields.addressSearch && !fields.addressOne && !fields.addressTwo) {
            errors.addressSearch = "Please enter an address.";
        }

        if (validateBillingFields) {
            if (!fields.firstNameBilling) {
                errors.firstNameBilling = "Please provide a first name.";
            }

            if (!fields.lastNameBilling) {
                errors.lastNameBilling = "Please provide a last name.";
            }

            if (!fields.cityBilling) {
                errors.cityBilling = "Please provide a city.";
            }

            if (!fields.countryBilling) {
                errors.countryBilling = "Please provide a country.";
            }

            if (!fields.postCodeBilling) {
                errors.postCodeBilling = "Please provide a post code.";
            }

            if (!fields.addressOneBilling) {
                errors.addressOneBilling = "Please provide an address.";
            }
        }

        return errors;
    }, [validateBillingFields]);

    const defaultFields: FieldList = useMemo(() => ({
        email: appState.user?.Email ||"",
        firstName: appState.user?.FirstName || "",
        lastName: appState.user?.Surname || "",
        phone: "",
        company: "",
        city: "",
        country: getCountryFromCode(countryCode.code),
        postCode: "",
        addressSearch: "",
        selectedAddressId: "",
        addressOne: "",
        addressTwo: "",
        state: "",
        phoneCode: countryCode.code,
        firstNameBilling: appState.user?.FirstName || "",
        lastNameBilling: appState.user?.LastName || "",
        cityBilling: "",
        countryBilling: getCountryFromCode(countryCode.code),
        postCodeBilling: "",
        addressOneBilling: "",
        stateBilling: "",
        streetNumber: "",
        streetNumberBilling: "",
    }), [appState.user, countryCode.code]);

    const { fields, errors, validate, setFields, onFieldChange } = useForm({
        fields: defaultFields,
        validator,
    });

    // Need to reset the fields if country code loaded after we set the fields
    useEffect(() => {
        if (!fields.country && defaultFields.country) {
            setFields(defaultFields);
        }
    }, [defaultFields, fields.country, setFields]);

    usePhoneNumberInput(
        fields.phone,
        fields.phoneCode,
        onFieldChange,
        countryCode.code.toUpperCase(),
    );

    const { data } = useQuery(getEventRegistration, {
        variables: {
            userCode: localStorage.getItem('userCode'),
            hasCheckedOut: false,
        },
        skip: !localStorage.getItem('userCode'),
    });

    useEffect(() => {
        if (prevEventData.current === data || !isEmpty(appState.user)) return;
        prevEventData.current = data;

        const mainRegistrant = data?.eventRegistration?.[0]?.registrants?.[0];

        const changeCustomFields = (
            customFields: Array<CustomField>,
            codesAndFieldNames: Array<{ code: string, fieldName: string }>,
        ) => {
            customFields.forEach((customField: CustomField) => {
                const codeAndFieldName = codesAndFieldNames.find((codeAndFieldName: { code: string, fieldName: string }) => (
                    codeAndFieldName.code === customField.code
                ));

                if (!!codeAndFieldName) {
                    onFieldChange(codeAndFieldName.fieldName, customField.value);
                }
            });
        }
        if (isEmpty(mainRegistrant)) {
            setFields(defaultFields);
        } else {
            Object.entries(mainRegistrant).forEach(([fieldName, value]: [fieldName: string, value: any]) => {
                if (fieldName === 'customFields' && isArray(value)) {
                    changeCustomFields(value, [
                        { code: 'country', fieldName: 'country' },
                        { code: 'address', fieldName: 'addressOne' },
                        { code: 'city', fieldName: 'city' },
                        { code: 'zip', fieldName: 'postCode' },
                        { code: 'school_name', fieldName: 'company' },
                        { code: 'phone', fieldName: 'phone' },
                        { code: 'mobilephone', fieldName: 'phone' },
                        { code: 'state', fieldName: 'state' },
                    ]);
                } else if (Object.keys(fields).includes(fieldName)) {
                    onFieldChange(fieldName, value);
                }
            });
        }
    }, [appState.user, fields, onFieldChange, data, setFields, defaultFields]);

    return {
        fields,
        errors,
        onFieldChange,
        validate,
    }
}

export default useDetailsForm;